import type { Route, RouteWaypoint } from '../backend/route';

/**
 * Returns the waypoint the watcher should be looking at right now.
 *
 *  - During journey playback (`progress` ∈ [0, 1]) it's the waypoint at the
 *    visible head — battery/speed/timestamp shown in the bottom sheet match
 *    the moment the map marker is over.
 *  - Otherwise it's the latest waypoint (live or post-arrival "where the
 *    traveller ended up").
 */
export function activeWaypoint(route: Route | null, progress: number | null): RouteWaypoint | null {
  if (!route || !route.waypoints || route.waypoints.length === 0) return null;
  const waypoints = route.waypoints;
  if (progress === null) {
    return waypoints[waypoints.length - 1] ?? null;
  }
  const idx = Math.max(0, Math.min(waypoints.length - 1, Math.ceil(waypoints.length * progress) - 1));
  return waypoints[idx] ?? null;
}

/**
 * Short clock format (HH:MM) for a waypoint timestamp. Used by the replay
 * button's "paused at …" label where date context isn't useful. Inherits
 * the same epoch-zero guard as `formatWaypointTime`.
 */
export function formatWaypointClock(time: string | Date | undefined): string | null {
  if (!time) return null;
  const date = time instanceof Date ? time : new Date(time);
  if (Number.isNaN(date.getTime())) return null;
  if (date.getFullYear() < 2010) return null;
  return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
}

export function formatWaypointTime(time: string | Date | undefined): string | null {
  if (!time) return null;
  const date = time instanceof Date ? time : new Date(time);
  if (Number.isNaN(date.getTime())) return null;
  // Guard against epoch-zero or otherwise nonsensical timestamps that
  // would otherwise render as "1 Jan 1970, 00:00:00" or similar. Real
  // ArriveSafe journeys all live in the smartphone era — anything before
  // 2010 is bad data on the wire (a missing `waypoint.time`, a zeroed
  // start-location timestamp, etc.) and is better hidden than shown.
  if (date.getFullYear() < 2010) return null;
  // Date + time so the watcher knows which day they're looking at — useful
  // when scrubbing back through an older route. Seconds included because a
  // short trip otherwise looks stationary as the playback head moves.
  return date.toLocaleString([], {
    day: 'numeric',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  });
}

/**
 * Distance travelled up to the active waypoint. Computed via Haversine over
 * the waypoint slice so it lives-updates during playback rather than always
 * showing the route total. Uses route.distance as the final-state authority
 * (the API computes it from a fuller dataset than we get over the wire).
 */
export function activeDistanceMeters(route: Route | null, progress: number | null): number | null {
  if (!route) return null;
  const waypoints = route.waypoints ?? [];
  if (waypoints.length < 2) {
    return progress === null ? (route.distance ?? null) : 0;
  }
  // Live or finished, no playback → trust the API's total.
  if (progress === null && route.distance && route.distance > 0) {
    return route.distance;
  }
  // Determine the slice end. progress === null means "all the way".
  const endIdx =
    progress === null
      ? waypoints.length
      : Math.max(1, Math.min(waypoints.length, Math.ceil(waypoints.length * progress)));
  let total = 0;
  for (let i = 1; i < endIdx; i++) {
    const a = waypoints[i - 1];
    const b = waypoints[i];
    if (!a || !b) continue;
    total += haversineMeters(a.lat, a.lon, b.lat, b.lon);
  }
  return total;
}

/**
 * Average speed across the whole journey, in m/s. Returns null if we don't
 * have enough signal to compute one — used by the post-arrival view, where
 * the last waypoint's instantaneous speed is misleading (often near zero
 * because the traveller stopped moving by the time the final fix landed).
 */
export function averageSpeedMps(route: Route | null): number | null {
  if (!route) return null;
  const distance = route.distance ?? null;
  const waypoints = route.waypoints ?? [];
  if (waypoints.length < 2) return null;
  // Pick a trustworthy start anchor. The first waypoint's `time` is often
  // bad (epoch-zero from the API's route-start.ts bug — see
  // formatWaypointTime). Fall back to route.created_at, then to the
  // second waypoint, in that order.
  const startMs = pickStartMs(route, waypoints);
  // End anchor: last waypoint with a usable time, falling back to
  // finished_at for finished routes.
  const endMs = pickEndMs(route, waypoints);
  if (startMs === null || endMs === null) return null;
  const durationS = (endMs - startMs) / 1000;
  if (durationS <= 0) return null;
  const totalMeters = distance && distance > 0 ? distance : activeDistanceMeters(route, null);
  if (!totalMeters || totalMeters <= 0) return null;
  return totalMeters / durationS;
}

/**
 * Journey-average speed in km/h for the post-arrival views. Prefers the
 * server's `average_speed` (api #180 — already km/h, computed from the full
 * waypoint set) over the client-side estimate, which only sees the
 * waypoints delivered to this session. Both call sites are post-arrival on
 * purpose: on a live route the server fields are snapshots from the last
 * read — don't wire these into a live ticker.
 */
export function displayAverageKmh(route: Route | null): number | null {
  const server = route?.average_speed;
  if (typeof server === 'number' && Number.isFinite(server) && server > 0) return server;
  const clientMps = averageSpeedMps(route);
  return clientMps === null ? null : clientMps * 3.6;
}

/**
 * Journey duration in seconds, preferring the server's `duration` (api
 * #180) with the created_at→finished_at spread as the pre-#180 fallback.
 */
export function displayDurationSeconds(route: Route | null): number | null {
  const server = route?.duration;
  if (typeof server === 'number' && Number.isFinite(server) && server > 0) return server;
  if (!route) return null;
  const created = parseValidMs(route.created_at);
  const finished = parseValidMs(route.finished_at ?? undefined);
  if (created === null || finished === null) return null;
  const seconds = (finished - created) / 1000;
  return seconds > 0 ? seconds : null;
}

const MIN_YEAR = 2010;

/**
 * Epoch-milliseconds for a wire timestamp, or null when it's missing,
 * unparsable, or pre-2010 garbage (see formatWaypointTime). Shared with
 * journey-event sorting so both views apply one validity rule.
 */
export function parseValidMs(time: string | Date | undefined): number | null {
  if (!time) return null;
  const ms = (time instanceof Date ? time : new Date(time)).getTime();
  if (!Number.isFinite(ms)) return null;
  if (new Date(ms).getFullYear() < MIN_YEAR) return null;
  return ms;
}

function pickStartMs(route: Route, waypoints: RouteWaypoint[]): number | null {
  const first = parseValidMs(waypoints[0]?.time);
  if (first !== null) return first;
  const created = parseValidMs(route.created_at);
  if (created !== null) return created;
  // Last-ditch: use the second waypoint's time if the first is corrupt.
  return parseValidMs(waypoints[1]?.time);
}

function pickEndMs(route: Route, waypoints: RouteWaypoint[]): number | null {
  for (let i = waypoints.length - 1; i >= 0; i--) {
    const ms = parseValidMs(waypoints[i]?.time);
    if (ms !== null) return ms;
  }
  return parseValidMs(route.finished_at ?? undefined);
}

const EARTH_RADIUS_M = 6_371_000;

function haversineMeters(lat1: number, lon1: number, lat2: number, lon2: number): number {
  const toRad = (deg: number) => (deg * Math.PI) / 180;
  const dLat = toRad(lat2 - lat1);
  const dLon = toRad(lon2 - lon1);
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLon / 2) ** 2;
  return 2 * EARTH_RADIUS_M * Math.asin(Math.min(1, Math.sqrt(a)));
}
